// 7th Form
import React, { useState, useEffect, useMemo } from 'react';
import no_Machine from '@/js/images/no-machine.svg';
import defaultImage from '@/js/images/default-machine.svg';
import noData1 from '@/js/images/no-data.svg';
import noData2 from '@/js/images/nodata.svg';
import noData3 from '@/js/images/nodata3.svg';
import noData4 from '@/js/images/nodata4.svg';
import noData5 from '@/js/images/nodata5.svg';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowDownLeftFromSquareIcon,
  Edit2,
  Image as ImageIcon,
  Plus,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { registerSupplierAction } from '../../../reduxstore/actions/authActions';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

// // Example data structure
// {
//   automation: 'Semi-Automatic',
//   machine_id: 'MACHINE_45095bb9-7583-4ee6-bc17-7b0cfee9300',
//   plant_id: 'PLANT_8ae50af6-23ef-199-b6c3-f05b731cfbed',
//   machine_name: 'Nm-322-VTTT-Rica',
//   plant_name: null,
//   make_and_model: 'Haas VF-4SS',
//   types_of_machine: 'Vertical Machining Center',
//   machine_capacity: '12000',
//   last_calibrated_on: '2024-01-20',
//   table_size: '50x40 inches',
//   up_time: '20 hours per day',
//   age_of_machine: '5 years',
//   maintenance_cycle: 'Monthly',
//   number_of_skilled_operators: 3,
//   acceptance_rate: '2%',
//   document_ids: [],
// },

const randomImages = [noData1, noData2, noData3, noData4, noData5];
const randomTexts = [
  'Opps! No such machines.',
  'Uh-oh! No findings',
  "Um, it seems you don't have this machine.",
  "Nope, we couldn't find this machine.",
  'We found nothing.',
];

const MachiningInformation = ({ setSectionId }) => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reduxData = useSelector(
    (state) => state?.authReducer?.registrationData,
  );

  const [localPlantInfo, setLocalPlantInfo] = useState(
    reduxData?.plant_info || [],
  );
  const [localMachineInfo, setLocalMachineInfo] = useState(
    reduxData?.machining_info || [],
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [randomNoData, setRandomNoData] = useState({
    image: randomImages[0],
    text: randomTexts[0],
  });
  const [noDataInitialized, setNoDataInitialized] = useState(false);

  // Update local state when Redux data changes
  useEffect(() => {
    if (reduxData?.plant_info) {
      setLocalPlantInfo(reduxData.plant_info);

      // Update machine info with new plant names
      if (reduxData?.machining_info && reduxData.plant_info) {
        const updatedMachineInfo = reduxData.machining_info.map((machine) => {
          if (machine.plant_id) {
            const associatedPlant = reduxData.plant_info.find(
              (plant) => plant.plant_id === machine.plant_id,
            );
            if (associatedPlant) {
              return {
                ...machine,
                plant_name: associatedPlant.plant_name,
              };
            }
          }
          return machine;
        });
        setLocalMachineInfo(updatedMachineInfo);
      }
    }

    if (reduxData?.machining_info) {
      setLocalMachineInfo((prevMachineInfo) => {
        // Only update if the machine IDs or structure has changed
        if (
          JSON.stringify(prevMachineInfo.map((m) => m.machine_id)) !==
          JSON.stringify(reduxData.machining_info.map((m) => m.machine_id))
        ) {
          return reduxData.machining_info;
        }
        return prevMachineInfo;
      });
    }
  }, [reduxData]);

  // Memoized filtered machines with updated dependencies
  const filteredMachines = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return localMachineInfo;
    }
    const nameMatches = [];
    const plantMatches = [];

    localMachineInfo.forEach((machine) => {
      const nameMatch = machine.machine_name?.toLowerCase().includes(term);
      const plantMatch = machine.plant_name?.toLowerCase().includes(term);

      if (nameMatch) {
        nameMatches.push(machine);
      } else if (plantMatch) {
        plantMatches.push(machine);
      }
    });

    return [...nameMatches, ...plantMatches];
  }, [searchTerm, localMachineInfo, reduxData?.plant_info]);

  useEffect(() => {
    if (filteredMachines.length === 0 && !noDataInitialized) {
      const randomIndex = Math.floor(Math.random() * randomImages.length);
      setRandomNoData({
        image: randomImages[randomIndex],
        text: randomTexts[randomIndex],
      });
      setNoDataInitialized(true);
    } else if (filteredMachines.length > 0) {
      setNoDataInitialized(false);
    }
  }, [filteredMachines, noDataInitialized]);

  return (
    <div className="w-full font-roboto h-full flex flex-col items-center justify-center text-gray-700">
      {localMachineInfo.length > 0 ? (
        <>
          <div className="max-w-6xl justify-between h-12 w-full px-8 font-semibold flex mb-6 items-center">
            <span className="text-2xl text-gray-600">
              {console.log(
                localMachineInfo,
                'Local Machine Info',
                localPlantInfo,
                'Local Plant Info',
              )}
              {filteredMachines.length}{' '}
              {filteredMachines.length === 1 ? 'Result' : 'Results'}
            </span>

            <div className="relative flex">
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="focus:ring-0 font-normal focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple/60 px-5 py-2 rounded-md mr-3 h-10 border w-72"
              />

              <button
                className="px-5 flex py-2 bg-lightPurple  border border-lightPurple h-10 text-white ease-in duration-100 text-base font-normal rounded-md  hover:text-darkerPurple hover:bg-white"
                onClick={() => setSectionId(9)}
              >
                <Plus size={20} className="mr-2 translate-y-0.5" />
                Add
              </button>
            </div>
          </div>
          {filteredMachines.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-4 pt-4 max-w-6xl">
              {filteredMachines.map((machine) => (
                <div
                  key={machine.plant_id}
                  className="bg-white border shadow-md min-w-[280px] gap-1 rounded-md p-5 w-full max-w-sm sm:max-w-[320px]"
                >
                  {console.log(filteredMachines, 'Filtered Machines')}
                  {/* Image Container - Centered */}
                  <div className="w-full flex justify-center">
                    <img
                      src={defaultImage}
                      alt="default Plant"
                      className="p-2 w-full max-w-[250px] h-auto object-cover rounded-md mb-1"
                    />
                  </div>
                  {/* Left-aligned content */}
                  <h2 className="text-xl font-semibold text-darkerPurple mb-2">
                    {machine.machine_name}
                  </h2>
                  <p className="text-gray-600 text-sm min-h-5">
                    <span className="font-medium">Plant: </span>
                    <span>
                      {machine.plant_name && machine.plant_name.trim() !== ''
                        ? machine.plant_name
                        : 'None'}
                    </span>
                  </p>
                  <Button
                    type="button"
                    className="mt-3 font-semibold flex w-full h-8 items-center px-5 py-2 text-sm bg-white border border-lightPurple text-lightPurple rounded-md hover:bg-lightPurple/95 hover:text-white transition-colors group"
                    onClick={() => setSectionId(9, machine.machine_id)}
                  >
                    <Edit2
                      size={20}
                      className="transition-transform duration-200 group-hover:rotate-360 cursor-pointer"
                    />
                    Edit
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 flex flex-col items-center justify-center">
              <h1 className="text-2xl font-semibold text-gray-600 mb-6">
                {randomNoData.text}
              </h1>
              <img src={randomNoData.image} alt="No Data Found" width={300} />
            </div>
          )}
        </>
      ) : (
        <div className="py-16">
          <h1 className="text-3xl font-semibold text-gray-600 text-center mb-6">
            No machines have been added so far.
          </h1>
          <img src={no_Machine} width={600} alt="no plant" />
          <div className="flex mt-6 justify-center items-center">
            <button
              className="mt-2 flex px-5 py-2 text-md bg-lightPurple border border-darkPurple text-white rounded-md hover:bg-white hover:text-darkerPurple transition-colors"
              onClick={() => setSectionId(9)}
            >
              <Plus size={20} className="mr-2 translate-y-0.5" />
              Add machines
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MachiningInformation;
